import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export function Section({ label, children, aside }: { label: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{label}</h4>
        {aside}
      </div>
      {children}
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  disabled?: boolean;
}

/**
 * Text input that autosaves on blur when its value changed (style guide §4.6).
 * `value` is the server value; local edits stay until the record id changes.
 */
export function TextField({
  id, label, value, disabled, multiline, placeholder, onCommit,
}: FieldProps & { value: string | null; multiline?: boolean; placeholder?: string; onCommit: (value: string) => void }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const commit = () => {
    if (draft !== (value ?? '')) onCommit(draft);
  };
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      {multiline ? (
        <Textarea id={id} value={draft} disabled={disabled} placeholder={placeholder} className="text-sm min-h-[80px]"
          onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
      ) : (
        <Input id={id} value={draft} disabled={disabled} placeholder={placeholder} className="text-sm h-8"
          onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
      )}
    </div>
  );
}

/**
 * Number input committed on blur. Empty clears the value (null); out-of-range or
 * non-numeric input is reverted instead of saved.
 */
export function NumberField({
  id, label, value, disabled, min, max, step = 'any', integer, onCommit,
}: FieldProps & {
  value: number | null; min?: number; max?: number; step?: string; integer?: boolean; onCommit: (value: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  useEffect(() => setDraft(value === null ? '' : String(value)), [value]);
  const commit = () => {
    const next = draft.trim() === '' ? null : Number(draft);
    const valid = next === null || (Number.isFinite(next)
      && (min === undefined || next >= min) && (max === undefined || next <= max) && (!integer || Number.isInteger(next)));
    if (!valid) {
      setDraft(value === null ? '' : String(value));
      return;
    }
    if (next !== value) onCommit(next);
  };
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} type="number" inputMode="decimal" value={draft} min={min} max={max} step={step} disabled={disabled}
        className="text-sm h-8 tabular-nums" onChange={(e) => setDraft(e.target.value)} onBlur={commit} />
    </div>
  );
}

export function SelectField<T extends string>({
  id, label, value, options, disabled, placeholder = 'Not set', allowEmpty, onChange,
}: FieldProps & {
  value: T | null; options: readonly { value: T; label: string }[]; placeholder?: string; allowEmpty?: boolean; onChange: (value: T | null) => void;
}) {
  const EMPTY = '__none__';
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Select value={value ?? EMPTY} disabled={disabled} onValueChange={(next) => onChange(next === EMPTY ? null : (next as T))}>
        <SelectTrigger id={id} className="h-8 text-sm"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {allowEmpty && <SelectItem value={EMPTY}>{placeholder}</SelectItem>}
          {options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// Same squashing as equipment_norm in SQL: "mpr n450" finds "MPR-N450FH-PA".
const squash = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '');

// cmdk's default filter is fuzzy; "phcbi" must list the PHCbi entries and nothing else.
// Every typed word has to appear in the label, in any order.
const matchesEveryWord = (_value: string, search: string, keywords: string[] = []) => {
  const label = squash(keywords.join(' '));
  return search.split(/\s+/).map(squash).filter(Boolean).every((word) => label.includes(word)) ? 1 : 0;
};

/** SelectField with a search box, for lists too long to scroll (hundreds of library models). */
export function SearchableSelectField<T extends string>({
  id, label, value, options, disabled, placeholder = 'Choose', searchPlaceholder = 'Search', emptyText = 'No match', onChange,
}: FieldProps & {
  value: T | null; options: readonly { value: T; label: string }[];
  placeholder?: string; searchPlaceholder?: string; emptyText?: string; onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button id={id} type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled}
            className="h-8 w-full justify-between px-3 text-sm font-normal">
            <span className="truncate">{selected?.label ?? placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command filter={matchesEveryWord}>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem key={option.value} value={option.value} keywords={[option.label]}
                    onSelect={() => { onChange(option.value); setOpen(false); }}>
                    <span className="truncate">{option.label}</span>
                    <Check className={cn('ml-auto h-4 w-4 shrink-0', option.value === value ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
